import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  CalendarEvent,
  ScheduleSummary,
} from "../../services/scheduling/Scheduling.types";
import { getScheduleColor } from "./calendar.utils";
import styles from "./ScheduleCalendar.module.css";

interface AppointmentPanelProps {
  event: CalendarEvent;
  schedules: ScheduleSummary[];
  onClose: () => void;
}

export default function AppointmentPanel({
  event,
  schedules,
  onClose,
}: AppointmentPanelProps) {
  const { t } = useTranslation();
  const eventSchedules = schedules.filter((schedule) =>
    event.scheduleIds.includes(schedule.id),
  );

  return (
    <aside className={styles.appointmentPanel} aria-label={t("calendar.details.title")}>
      <div className={styles.appointmentPanelHeader}>
        <div>
          <span className={styles.detailEyebrow}>{t("calendar.details.title")}</span>
          <h3>{event.title}</h3>
        </div>
        <button
          type="button"
          aria-label={t("common.close")}
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <span className={styles.statusBadge}>
        {t(`calendar.status.${event.status}`, { defaultValue: event.status })}
      </span>

      <dl className={styles.detailList}>
        <div>
          <dt>{t("calendar.details.date")}</dt>
          <dd>{dayjs(event.start).format("dddd D MMMM YYYY")}</dd>
        </div>
        <div>
          <dt>{t("calendar.details.time")}</dt>
          <dd>
            {dayjs(event.start).format("HH:mm")} – {dayjs(event.end).format("HH:mm")}
          </dd>
        </div>
        {event.locationDisplay && (
          <div>
            <dt>{t("calendar.details.location")}</dt>
            <dd>{event.locationDisplay}</dd>
          </div>
        )}
        {event.participantDisplays.length > 0 && (
          <div>
            <dt>{t("calendar.details.participants")}</dt>
            <dd>{event.participantDisplays.join(", ")}</dd>
          </div>
        )}
        {eventSchedules.length > 0 && (
          <div>
            <dt>{t("calendar.details.schedules")}</dt>
            <dd className={styles.detailSchedules}>
              {eventSchedules.map((schedule) => (
                <span key={schedule.id}>
                  <i
                    style={{
                      backgroundColor: getScheduleColor(schedule.id, schedules),
                    }}
                  />
                  {schedule.kind === "unlinked"
                    ? t("calendar.unlinked")
                    : schedule.title}
                </span>
              ))}
            </dd>
          </div>
        )}
        {(event.description || event.comment) && (
          <div>
            <dt>{t("calendar.details.notes")}</dt>
            <dd>{event.description || event.comment}</dd>
          </div>
        )}
        {event.patientInstruction && (
          <div>
            <dt>{t("calendar.details.instructions")}</dt>
            <dd>{event.patientInstruction}</dd>
          </div>
        )}
      </dl>
    </aside>
  );
}
